import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '../../../config';
import { accessToken } from '../../../configuration/tokenHandling/accessToken';
import type { Bookable } from '../bookable/redux/types';
import type { FreeTrialMappedResponse } from '../freeTrial/redux/types';
import { mapApiBookables } from './bookable/bookableMapper';
import { mapApiFreeTrialStatus } from './freeTrial/freeTrialMapper';

const localVariationHeader = 'X-Local-Variation-Params';
const localMockBackendHosts = new Set(['localhost:3037', '127.0.0.1:3037']);

const isLocalMockBackend = () => {
    if (!import.meta.env.DEV || !config.backend.MARKETPLACE_SERVICE) {
        return false;
    }

    try {
        return localMockBackendHosts.has(new URL(config.backend.MARKETPLACE_SERVICE).host);
    } catch {
        return false;
    }
};

export const marketplaceApi = createApi({
    reducerPath: 'marketplaceApi',
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.MARKETPLACE_SERVICE,
        prepareHeaders: headers => {
            headers.set('Authorization', `Bearer ${accessToken.getAccessToken()}`);
            if (isLocalMockBackend()) {
                headers.set(localVariationHeader, window.location.search);
            }
            return headers;
        },
    }),
    tagTypes: ['FreeTrial', 'Bookables'],
    endpoints: builder => ({
        getFreeTrialStatus: builder.query<FreeTrialMappedResponse, void>({
            query: () => ({
                url: '/free-trial',
            }),
            transformResponse: mapApiFreeTrialStatus,
            providesTags: ['FreeTrial'],
        }),
        getBookables: builder.query<Bookable, { sku: string; level: string; resourceId: string | undefined }>({
            query: ({ sku, level, resourceId }) => ({
                url: `/activations/${sku}/bookable`,
                params: { level, resource_ids: resourceId },
            }),
            transformResponse: (apiBookables: unknown, meta, { resourceId }): Bookable =>
                mapApiBookables(apiBookables, resourceId),
            providesTags: ['Bookables'],
        }),
    }),
});

export const { useGetFreeTrialStatusQuery, useLazyGetBookablesQuery } = marketplaceApi;
